import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UnitsTable, UnitsMutateDrawer, DeleteUnitDialog } from '@/components/units'
import { useUnits, useDeleteUnit } from '@/hooks/useUnits'
import { useAppContext } from '@/hooks'

/**
 * Units management page
 * Displays list of all units across buildings with CRUD operations
 */
export function UnitsPage() {
  // Get location from URL context
  const { locationId, scope } = useAppContext()

  // Sheet/Dialog state
  const [sheetState, setSheetState] = useState<{
    open: boolean
    unitId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    unitCode: string
  } | null>(null)

  // Fetch units, filtered by location when in location scope
  const unitFilters = scope === 'location' && locationId ? { locationId } : undefined
  const { data: units, isLoading } = useUnits(unitFilters)

  // Delete mutation
  const deleteUnit = useDeleteUnit()

  const handleCreate = () => {
    setSheetState({ open: true, unitId: undefined })
  }

  const handleEdit = (id: string) => {
    setSheetState({ open: true, unitId: id })
  }

  const handleDelete = (id: string, unitCode: string) => {
    setDeleteTarget({ id, unitCode })
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetState((prev) => ({
      open,
      unitId: open ? prev.unitId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteUnit.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Units</h1>
          <p className="text-muted-foreground">
            Manage leasable spaces within your buildings
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Unit
        </Button>
      </div>

      {/* Units Table */}
      <UnitsTable
        data={units || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Create/Edit Unit Drawer */}
      <UnitsMutateDrawer
        open={sheetState.open}
        onOpenChange={handleSheetOpenChange}
        unitId={sheetState.unitId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteUnitDialog
        open={!!deleteTarget}
        onOpenChange={handleCloseDeleteDialog}
        unitCode={deleteTarget?.unitCode ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteUnit.isPending}
      />
    </div>
  )
}
